import type {
  AutoContinuePolicy,
  CommandReceipt,
  ControllerState,
  DaemonHealth,
  HudSettings,
  ManagedAttention,
  ManagedArtifact,
  ManagedAssignment,
  ManagedChangeOperation,
  ManagedChangeSet,
  ManagedDecision,
  ManagedEntityPatch,
  ManagedDispatch,
  ManagedGraphEntity,
  ManagedMaterial,
  ManagedMaterialEntityRef,
  ManagedMaterialPatch,
  ManagedOptionalFieldPatch,
  ManagedProposalBasis,
  ManagedQuestion,
  ManagedRelation,
  ManagedRound,
  ManagedReport,
  ManagedRun,
  ManagedRuntimeResource,
  ManagedSessionLeg,
  ManagedWorkIdentity,
  ManagedWorkScopeRef,
  ManagedWorkSnapshot,
  ManagedTask,
  NotificationItem,
  OrniEvent,
  PermissionMode,
  ProviderDescriptor,
  ProviderConversationPage,
  ProviderHistoryCatalog,
  ProviderConversationSummary,
  ProviderHistoryScanResult,
  ProviderInventory,
  ProviderInventoryItem,
  ProviderTerminalQuickCommand,
  SessionAttention,
  SessionDetail,
  SessionStatus,
  SessionSummary,
  SessionVisibility,
  TerminalLine,
  TerminalLineKind,
  TerminalSnapshot,
  WorkGraphPage,
  WorkMutationBatch,
  WorkMutationOperation,
  WorkMutationPage,
  WorkSourceSelection,
  WorkspaceRef,
} from "./types";
import {
  decodeAgentIdentity,
  decodeProviderId,
} from "@orni/sdk-typescript";

export type JsonObject = Record<string, unknown>;

const sessionStatuses: SessionStatus[] = [
  "starting",
  "running",
  "waiting",
  "stopping",
  "stopped",
  "exited",
  "error",
  "archived",
];
const attentions: SessionAttention[] = ["none", "info", "action", "critical"];
const visibilities: SessionVisibility[] = ["attached", "background", "hidden"];
const terminalKinds: TerminalLineKind[] = [
  "input",
  "output",
  "reasoning",
  "tool",
  "success",
  "warning",
  "error",
  "system",
];

export function object(value: unknown): JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as JsonObject)
    : {};
}

export function array(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringArray(value: unknown): string[] {
  return array(value).filter((item): item is string => typeof item === "string");
}

export function unwrap(value: unknown, ...keys: string[]): JsonObject {
  let result = object(value);
  for (const key of keys) {
    if (result[key] !== undefined) return object(result[key]);
  }
  return result;
}

function text(record: JsonObject, keys: string[], fallback = ""): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return fallback;
}

function nullableText(record: JsonObject, keys: string[]): string | null {
  const value = text(record, keys);
  return value || null;
}

function numberValue(record: JsonObject, keys: string[], fallback = 0): number {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return fallback;
}

function nullableNumber(record: JsonObject, keys: string[]): number | null {
  for (const key of keys) if (typeof record[key] === "number") return record[key] as number;
  return null;
}

function bool(record: JsonObject, keys: string[], fallback = false): boolean {
  for (const key of keys) {
    if (typeof record[key] === "boolean") return record[key] as boolean;
  }
  return fallback;
}

function enumValue<T extends string>(
  record: JsonObject,
  keys: string[],
  values: readonly T[],
  fallback: T,
): T {
  const value = text(record, keys) as T;
  return values.includes(value) ? value : fallback;
}

function timestamp(record: JsonObject, keys: string[]): string {
  return text(record, keys, new Date(0).toISOString());
}

export function mapProvider(value: unknown): ProviderDescriptor {
  const source = object(value);
  const rawId = text(source, ["id", "provider_id", "provider"], "codex");
  const identity = decodeAgentIdentity({ ...source, provider_id: rawId });
  // Capabilities can advertise a future Agent before Desktop ships its
  // execution adapter. Keep that identity readable instead of silently
  // presenting it as Codex.
  const projectedProviderId = decodeProviderId(rawId);
  const id = identity.legacyProviderId ?? "codex";
  const capabilities = object(source.capabilities);
  return {
    id,
    projectedProviderId,
    agentId: identity.agentId,
    cliVariantId: identity.cliVariantId,
    name: text(source, ["name", "display_name"], rawId),
    available: bool(source, ["available", "enabled"], true),
    version: nullableText(source, ["version"]),
    models: array(source.models).filter((item): item is string => typeof item === "string"),
    capabilities: {
      semanticEvents: bool(capabilities, ["semantic_events", "semanticEvents"]),
      resume: bool(capabilities, ["resume"]),
      hiddenSessions: bool(capabilities, ["hidden_sessions", "hiddenSessions"]),
      controllerLease: bool(capabilities, ["controller_lease", "controllerLease"]),
    },
  };
}

export function mapProviderInventoryItem(
  value: unknown,
): ProviderInventoryItem {
  const source = object(value);
  const rawProviderId = text(
    source,
    ["provider_id", "providerId"],
    text(source, ["agent_id", "agentId"], "codex"),
  );
  const identity = decodeAgentIdentity({ ...source, provider_id: rawProviderId });
  const projectedProviderId = decodeProviderId(rawProviderId);
  // Keep the raw identity in providerId at runtime. The local UI still types
  // the field as the legacy command key, so unknown values are excluded from
  // create/settings actions by their legacyProviderId state.
  const providerId = identity.legacyProviderId ?? "codex";
  const legacyProviderId = identity.legacyProviderId;
  const terminalQuickCommands: ProviderTerminalQuickCommand[] = array(
    source.terminal_quick_commands ?? source.terminalQuickCommands,
  ).flatMap((value) => {
    const row = object(value);
    const command = text(row, ["command"]);
    if (command.length > 96 || !(/^(?:\/[a-z][a-z0-9-]*(?:[ \t]+[A-Za-z0-9._:\/@+-]+)*|!)$/.test(command))) return [];
    const executionValue = row.execution;
    const execution = executionValue === "draft" || executionValue === "submit" || executionValue === "interactive" || executionValue === "shell"
      ? executionValue
      : bool(row, ["auto_submit", "autoSubmit"], command !== "/skills") ? "submit" : "interactive";
    const argumentHint = typeof row.argument_hint === "string"
      ? row.argument_hint
      : typeof row.argumentHint === "string" ? row.argumentHint : null;
    return [{
      command,
      execution,
      argumentHint,
      autoSubmit: row.execution === undefined
        ? bool(row, ["auto_submit", "autoSubmit"], command !== "/skills")
        : execution === "submit",
    }];
  });
  return {
    providerId,
    agentId: identity.agentId,
    cliVariantId: identity.cliVariantId,
    legacyProviderId,
    projectedProviderId,
    adapterId: nullableText(source, ["adapter_id", "adapterId"]),
    adapterVersion: nullableText(source, ["adapter_version", "adapterVersion"]),
    displayName: text(source, ["display_name", "displayName"], rawProviderId),
    candidate: bool(source, ["candidate"], false),
    installed: bool(source, ["installed"]),
    enabled: bool(source, ["enabled"]),
    supportsNative: bool(
      source,
      ["supports_native", "supportsNative"],
      legacyProviderId !== null,
    ),
    supportsOrniWork: bool(
      source,
      ["supports_orni_work", "supportsOrniWork"],
      legacyProviderId !== null && legacyProviderId !== "opencode",
    ),
    terminalQuickCommands,
    executablePath: nullableText(source, ["executable_path", "executablePath"]),
    configuredExecutablePath: nullableText(source, [
      "configured_executable_path",
      "configuredExecutablePath",
    ]),
    version: nullableText(source, ["version"]),
    homeDirectory: text(source, ["home_directory", "homeDirectory"]),
    configuredHomeDirectory: nullableText(source, [
      "configured_home_directory",
      "configuredHomeDirectory",
    ]),
    apiBaseUrl: nullableText(source, ["api_base_url", "apiBaseUrl"]),
    protocolId: nullableText(source, ["protocol_id", "protocolId"]),
    authKind: nullableText(source, ["auth_kind", "authKind"]),
    apiKeyConfigured: bool(source, ["api_key_configured", "apiKeyConfigured"]),
    defaultModel: nullableText(source, ["default_model", "defaultModel"]),
    installCommand: text(source, ["install_command", "installCommand"]),
    upgradeCommand: text(source, ["upgrade_command", "upgradeCommand"]),
    lastCheckedAt: timestamp(source, ["last_checked_at", "lastCheckedAt"]),
    revision: numberValue(source, ["revision"], 1),
  };
}

export function mapProviderInventory(value: unknown): ProviderInventory {
  const source = object(value);
  return {
    providers: array(source.providers).map(mapProviderInventoryItem),
    checkedAt: timestamp(source, ["checked_at", "checkedAt"]),
  };
}

export function mapWorkspace(value: unknown): WorkspaceRef {
  const source = object(value);
  const id = text(source, ["id", "workspace_id"]);
  const path = text(source, ["path", "root", "local_path"], id || "Unavailable");
  return {
    id: id || path,
    name: text(
      source,
      ["name", "display_name"],
      path.split("/").filter(Boolean).at(-1) ?? path,
    ),
    path,
    branch: nullableText(source, ["branch", "git_branch"]),
  };
}

export function mapHealth(value: unknown, latencyMs: number): DaemonHealth {
  const source = unwrap(value, "daemon", "health");
  const state = text(source, ["state", "status"], "online");
  return {
    state: state === "offline" ? "offline" : state === "degraded" ? "degraded" : "online",
    version: text(source, ["version"], "unknown"),
    deviceName: text(source, ["device_name", "deviceName", "hostname"], "Local device"),
    deviceId: text(
      source,
      ["device_id", "deviceId", "instance_id", "daemon_instance_id"],
      "local-daemon",
    ),
    transport: "local",
    latencyMs,
    startedAt: nullableText(source, ["started_at", "startedAt"]),
    lastSeenAt: nullableText(source, ["last_seen_at", "lastSeenAt"]) ?? new Date().toISOString(),
    managedSessions: numberValue(source, ["managed_sessions", "managedSessions"]),
    activeSessions: numberValue(source, ["active_sessions", "activeSessions"]),
    message: nullableText(source, ["message", "detail"]),
  };
}

function mapAutoContinue(value: unknown): AutoContinuePolicy {
  const source = object(value);
  return {
    enabled: bool(source, ["enabled"], true),
    recoveryEnabled: bool(source, ["recovery_enabled", "recoveryEnabled"], true),
    retryWindowSeconds: numberValue(source, ["retry_window_seconds", "retryWindowSeconds"], 180),
    retryForever: bool(source, ["retry_forever", "retryForever"]),
    retryIntervalSeconds: numberValue(source, ["retry_interval_seconds", "retryIntervalSeconds"], 5),
    attemptsUsed: numberValue(source, ["attempts_used", "attemptsUsed"]),
    recoveryStartedAt: nullableText(source, ["recovery_started_at", "recoveryStartedAt"]),
    lastRecoveryError: nullableText(source, ["last_recovery_error", "lastRecoveryError"]),
    recoveryHistory: array(source.recovery_history ?? source.recoveryHistory).map((item) => {
      const record = object(item);
      const outcome = text(record, ["outcome"], "scheduled") as AutoContinuePolicy["recoveryHistory"][number]["outcome"];
      return {
        cause: text(record, ["cause"]),
        error: text(record, ["error"]),
        attemptedAt: text(record, ["attempted_at", "attemptedAt"]),
        attemptNumber: numberValue(record, ["attempt_number", "attemptNumber"]),
        outcome: ["scheduled", "dispatched", "succeeded", "cancelled", "failed"].includes(outcome) ? outcome : "scheduled",
        detail: nullableText(record, ["detail"]),
        completedAt: nullableText(record, ["completed_at", "completedAt"]),
      };
    }),
  };
}

function mapController(value: unknown, generation: number): ControllerState {
  const source = object(value);
  const mode = text(source, ["mode"], "unavailable") as ControllerState["mode"];
  return {
    mode: ["available", "owned", "held-elsewhere", "unavailable"].includes(mode)
      ? mode
      : "unavailable",
    holderLabel: nullableText(source, ["holder_label", "holderLabel"]),
    leaseId: nullableText(source, ["lease_id", "leaseId"]),
    expiresAt: nullableText(source, ["expires_at", "expiresAt"]),
    generation: numberValue(source, ["generation"], generation),
  };
}

export function mapSessionSummary(value: unknown): SessionSummary {
  const root = object(value);
  const source = unwrap(root, "session");
  const attempt = object(root.current_attempt ?? source.current_attempt);
  const id = text(source, ["id", "session_id"]);
  const workspace = mapWorkspace(source.workspace ?? {
    id: source.workspace_id,
    path: source.workspace_path,
    name: source.workspace_name,
    branch: source.branch,
  });
  const nextGeneration = numberValue(source, ["next_generation"], 1);
  const generation = numberValue(attempt, ["generation"], Math.max(1, nextGeneration - 1));
  const providerSelection = object(source.provider_selection);
  const rawProvider = text(
    source,
    ["provider", "provider_id"],
    text(providerSelection, ["provider_id"], "codex"),
  );
  const identity = decodeAgentIdentity(
    { ...providerSelection, provider_id: rawProvider },
    "session.provider_selection.identity",
  );
  const projectedProviderId = decodeProviderId(rawProvider);
  const provider = identity.legacyProviderId ?? "codex";
  return {
    id,
    shortId: text(source, ["short_id", "shortId"], id.slice(0, 8)),
    title: text(source, ["title", "name"], `${identity.agentId} session`),
    provider,
    nativeTerminalOnly: array(source.tags).includes("origin:provider-history"),
    projectedProviderId,
    agentId: identity.agentId,
    cliVariantId: identity.cliVariantId,
    // Historical snapshots may still carry the retired management mode. The
    // current product exposes all sessions as Native after Work retirement.
    managementMode: "native",
    model: text(
      source,
      ["model", "model_id"],
      text(providerSelection, ["integration_mode"], "Provider default"),
    ),
    status: mapSessionStatus(source, attempt),
    attention: enumValue(source, ["attention"], attentions, "none"),
    visibility: enumValue(source, ["visibility"], visibilities, "background"),
    workspace,
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
    lastActivityAt: timestamp(source, ["last_activity_at", "lastActivityAt", "updated_at"]),
    unreadCount: numberValue(source, ["unread_count", "unreadCount"]),
    generation,
    activeStageId: nullableText(source, ["active_stage_id", "activeStageId"]),
    taskProgress: typeof source.task_progress === "number"
      ? source.task_progress
      : typeof source.taskProgress === "number" ? source.taskProgress : null,
    autoContinue: mapAutoContinue(source.auto_continue ?? source.autoContinue),
    controller: mapController(source.controller, generation),
    preview: text(source, ["preview", "summary"], "Managed by local Daemon"),
    tags: array(source.tags).filter((item): item is string => typeof item === "string"),
  };
}

export function mapTerminalLine(value: unknown, fallbackSequence: number): TerminalLine {
  if (typeof value === "string") {
    return {
      id: `terminal-${fallbackSequence}`,
      sequence: fallbackSequence,
      timestamp: new Date(0).toISOString(),
      kind: "output",
      text: value,
    };
  }
  const source = object(value);
  const sequence = numberValue(source, ["sequence", "seq"], fallbackSequence);
  return {
    id: text(source, ["id", "line_id"], `terminal-${sequence}`),
    sequence,
    timestamp: timestamp(source, ["timestamp", "occurred_at", "created_at"]),
    kind: enumValue(source, ["kind", "type"], terminalKinds, "output"),
    text: text(source, ["text", "content", "plain_text"]),
  };
}

export function mapTerminal(value: unknown, sessionId: string, generation: number): TerminalSnapshot {
  const source = unwrap(value, "terminal", "terminal_snapshot", "snapshot");
  const lines = array(source.lines ?? source.scrollback).map(mapTerminalLine);
  return {
    sessionId,
    generation: numberValue(source, ["generation"], generation),
    firstSequence: numberValue(source, ["first_sequence", "firstSequence"], lines[0]?.sequence ?? 0),
    lastSequence: numberValue(source, ["last_sequence", "lastSequence", "watermark"], lines.at(-1)?.sequence ?? 0),
    truncated: bool(source, ["truncated"]),
    hasEarlier: bool(source, ["has_earlier", "hasEarlier"]),
    lines,
  };
}

export function mapReceipt(value: unknown, command: string): CommandReceipt {
  const source = unwrap(value, "receipt", "outcome", "command");
  const rawStatus = text(source, ["status", "outcome"], "accepted");
  const status = rawStatus === "applied" ? "succeeded" : rawStatus === "rejected"
    ? "rejected" : ["indeterminate", "expired", "cancelled"].includes(rawStatus)
      ? "failed" : rawStatus;
  const receiptError = object(source.error);
  return {
    id: text(source, ["id", "receipt_id", "command_id"]),
    command: text(source, ["command", "operation"], command),
    status: ["accepted", "running", "succeeded", "failed", "rejected"].includes(status)
      ? (status as CommandReceipt["status"])
      : "accepted",
    requestedAt: timestamp(source, ["requested_at", "created_at", "recorded_at"]),
    completedAt: nullableText(source, ["completed_at", "occurred_at", "recorded_at"]),
    detail: text(
      source,
      ["detail", "message"],
      text(receiptError, ["message"], "Command accepted by the local Daemon."),
    ),
  };
}

export function mapSessionDetail(value: unknown): SessionDetail {
  const root = object(value);
  const source = unwrap(root, "session");
  const summary = mapSessionSummary(root);
  const attempt = object(root.current_attempt ?? source.current_attempt);
  const defaultHud: HudSettings = {
    enabled: false,
    size: "medium",
    position: "right-top",
    showSessionStatus: true,
    showTaskProgress: true,
    showNotifications: true,
  };
  return {
    ...summary,
    controller: summary.controller.mode === "unavailable" ? {
      mode: "available",
      holderLabel: null,
      leaseId: null,
      expiresAt: null,
      generation: summary.generation,
    } : summary.controller,
    processAttemptId: nullableText(attempt, ["process_attempt_id", "id"]),
    permissionMode: enumValue(
      source,
      ["permission_mode", "permissionMode"],
      ["read-only", "workspace-write", "full-access"] satisfies PermissionMode[],
      "workspace-write",
    ),
    terminal: mapTerminal(source.terminal ?? source.terminal_snapshot, summary.id, summary.generation),
    facts: [],
    checkpoints: [],
    stages: [],
    claims: [],
    evidence: [],
    reviews: [],
    receipts: array(source.receipts).map((item) => mapReceipt(item, "session.command")),
    hud: defaultHud,
    unavailableFeatures: [
      { id: "continuity", label: "Facts and checkpoints", reason: "The local transport has no continuity endpoint yet." },
      { id: "hud", label: "HUD settings", reason: "HUD publication is not exposed by this transport slice." },
      { id: "archive", label: "Archive", reason: "Archive is not exposed by this transport slice." },
    ],
  };
}

function mapSessionStatus(source: JsonObject, attempt: JsonObject): SessionStatus {
  const explicit = text(source, ["status", "state"]);
  const processState = text(attempt, ["process_state"]);
  const desiredState = text(source, ["desired_state"]);
  const autoContinue = object(source.auto_continue ?? source.autoContinue);
  // A retryable startup failure is an in-flight recovery, not a terminal
  // session error. Keep the workspace connected while the Daemon retries.
  if (
    bool(autoContinue, ["enabled"], true) &&
    bool(autoContinue, ["recovery_enabled", "recoveryEnabled"], true) &&
    nullableText(autoContinue, ["recovery_started_at", "recoveryStartedAt"])
  ) {
    return "starting";
  }
  // The session's persisted status can briefly lag behind the current
  // ProcessAttempt while a start/recovery command is being applied. Once an
  // attempt exists, use its lifecycle state first; otherwise a stale
  // `stopped` value makes a live Provider look like an inactive session.
  if (processState === "planned" || processState === "spawning") return "starting";
  if (processState === "running") return "running";
  if (processState === "stopping") return "stopping";
  if (processState === "exited") return desiredState === "stopped" ? "stopped" : "exited";
  if (processState === "failed_to_start" || processState === "lost") return "error";
  if (sessionStatuses.includes(explicit as SessionStatus)) return explicit as SessionStatus;
  return desiredState === "deleted" ? "archived" : "stopped";
}

export function mapNotification(value: unknown): NotificationItem {
  const source = unwrap(value, "notification");
  const level = text(source, ["level", "severity"], "info") as NotificationItem["level"];
  return {
    id: text(source, ["id", "notification_id"]),
    level: ["info", "success", "warning", "error"].includes(level) ? level : "info",
    title: text(source, ["title"], "Daemon notification"),
    body: text(source, ["body", "message"]),
    sessionId: nullableText(source, ["session_id", "sessionId"]),
    createdAt: timestamp(source, ["created_at", "occurred_at"]),
    read: bool(source, ["read"]),
    actionLabel: nullableText(source, ["action_label", "actionLabel"]),
  };
}

export function mapManagedWork(value: unknown): ManagedWorkSnapshot {
  const source = object(value);
  const contextManifest = object(source.context_manifest ?? source.contextManifest);
  return {
    sessionId: text(source, ["session_id", "sessionId"]),
    contextManifest: Object.keys(contextManifest).length ? {
      id: text(contextManifest, ["context_manifest_id", "id"]),
      workId: text(contextManifest, ["work_id", "workId"]),
      scope: mapManagedWorkScope(contextManifest.scope),
      graphRevision: numberValue(contextManifest, ["graph_revision", "graphRevision"]),
      sourceSessionId: text(contextManifest, ["source_session_id", "sourceSessionId"]),
      executionSessionId: text(contextManifest, ["execution_session_id", "executionSessionId"]),
      childWorkId: text(contextManifest, ["child_work_id", "childWorkId"]),
      assignmentId: text(contextManifest, ["assignment_id", "assignmentId"]),
    } : null,
    work: source.work ? mapManagedWorkIdentity(source.work) : null,
    rounds: array(source.rounds).map(mapManagedRound),
    materials: array(source.materials).map(mapManagedMaterial),
    entities: array(source.entities).map(mapManagedGraphEntity),
    relations: array(source.relations).map(mapManagedRelation),
    materialEntityRefs: array(source.material_entity_refs ?? source.materialEntityRefs)
      .map(mapManagedMaterialEntityRef),
    changeSets: array(source.change_sets ?? source.changeSets).map(mapManagedChangeSet),
    decisions: array(source.decisions).map(mapManagedDecision),
    questions: array(source.questions).map(mapManagedQuestion),
    assignments: array(source.assignments).map(mapManagedAssignment),
    artifacts: array(source.artifacts).map(mapManagedArtifact),
    sessionLegs: array(source.session_legs ?? source.sessionLegs).map(mapManagedSessionLeg),
    runtimeResources: array(source.runtime_resources ?? source.runtimeResources)
      .map(mapManagedRuntimeResource),
    graphRevision: numberValue(source, ["graph_revision", "graphRevision"]),
    graphTruncated: bool(source, ["graph_truncated", "graphTruncated"]),
    task: source.task ? mapManagedTask(source.task) : null,
    run: source.run ? mapManagedRun(source.run) : null,
    dispatches: array(source.dispatches).map(mapManagedDispatch),
    reports: array(source.reports).map(mapManagedReport),
    attentions: array(source.attentions).map(mapManagedAttention),
    sourceSelections: array(source.source_selections ?? source.sourceSelections).map(mapWorkSourceSelection),
    revision: numberValue(source, ["revision"]),
  };
}

export function mapWorkSourceSelection(value: unknown): WorkSourceSelection {
  const source = object(value);
  return {
    id: text(source, ["source_selection_id", "id"]),
    workspacePath: text(source, ["workspace_path", "workspacePath"]),
    includeCurrentConversation: bool(source, ["include_current_conversation", "includeCurrentConversation"]),
    conversations: array(source.conversations).map((item) => {
      const conversation = object(item);
      return {
        provider: text(conversation, ["provider"], "codex") as WorkSourceSelection["conversations"][number]["provider"],
        conversationId: text(conversation, ["conversation_id", "conversationId"]),
      };
    }),
    sources: {
      documents: bool(object(source.sources), ["documents"]),
      code: bool(object(source.sources), ["code"]),
      git: bool(object(source.sources), ["git"]),
      other: bool(object(source.sources), ["other"]),
    },
    request: text(source, ["request"]),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    revision: numberValue(source, ["revision"], 1),
  };
}

function mapProviderConversationSummary(value: unknown, historyHomeDirectory?: string): ProviderConversationSummary {
  const source = object(value);
  const rawProvider = text(source, ["provider", "provider_id"], "codex");
  return {
    sourceId: text(source, ["source_id", "sourceId"]),
    // Existing history import controls only execute legacy providers. Keep the
    // opaque source separately so a future Agent is never rendered as Codex.
    provider: (decodeAgentIdentity({ ...source, provider_id: rawProvider }).legacyProviderId ?? "codex") as ProviderConversationSummary["provider"],
    projectedProviderId: decodeProviderId(rawProvider),
    historyHomeDirectory,
    conversationId: text(source, ["conversation_id", "conversationId"]),
    workspacePath: text(source, ["workspace_path", "workspacePath"]),
    title: text(source, ["title"], "未命名会话"),
    preview: text(source, ["preview"]),
    messageCount: numberValue(source, ["message_count", "messageCount"]),
    userMessageCount: numberValue(source, ["user_message_count", "userMessageCount"]),
    summaryTruncated: bool(source, ["summary_truncated", "summaryTruncated"]),
    startedAt: nullableText(source, ["started_at", "startedAt"]),
    updatedAt: nullableText(source, ["updated_at", "updatedAt"]),
    resumeSupported: bool(source, ["resume_supported", "resumeSupported"]),
    importedSessionId: nullableText(source, ["imported_session_id", "importedSessionId"]),
    importedAt: nullableText(source, ["imported_at", "importedAt"]),
  };
}

export function mapProviderConversationPage(value: unknown): ProviderConversationPage {
  const source = object(value);
  return {
    conversation: mapProviderConversationSummary(source.conversation),
    messages: array(source.messages).map((item) => {
      const message = object(item);
      return {
        messageId: text(message, ["message_id", "messageId"]),
        ordinal: numberValue(message, ["ordinal"]),
        role: text(message, ["role"], "system") as ProviderConversationPage["messages"][number]["role"],
        text: text(message, ["text"]),
        createdAt: nullableText(message, ["created_at", "createdAt"]),
      };
    }),
    nextCursor: nullableNumber(source, ["next_cursor", "nextCursor"]),
  };
}

export function mapProviderHistoryScan(value: unknown): ProviderHistoryScanResult {
  const source = object(value);
  return {
    workspacePath: text(source, ["workspace_path", "workspacePath"]),
    conversations: array(source.conversations).map((conversation) =>
      mapProviderConversationSummary(conversation),
    ),
  };
}

export function mapProviderHistoryCatalog(value: unknown): ProviderHistoryCatalog {
  const source = object(value);
  const mapped: ProviderHistoryCatalog = {
    conversationCount: numberValue(source, ["conversation_count", "conversationCount"]),
    providers: array(source.providers).map((rawProvider) => {
      const provider = object(rawProvider);
      const rawProviderId = text(provider, ["provider", "provider_id"], "codex");
      const identity = decodeAgentIdentity({ ...provider, provider_id: rawProviderId });
      return {
        provider: (identity.legacyProviderId ?? "codex") as ProviderHistoryCatalog["providers"][number]["provider"],
        projectedProviderId: decodeProviderId(rawProviderId),
        homeDirectory: text(provider, ["home_directory", "homeDirectory"]),
        conversationCount: numberValue(provider, ["conversation_count", "conversationCount"]),
        error: nullableText(provider, ["error"]),
        workspaces: array(provider.workspaces).map((rawWorkspace) => {
          const workspace = object(rawWorkspace);
          return {
            workspacePath: text(workspace, ["workspace_path", "workspacePath"]),
            displayName: text(workspace, ["display_name", "displayName"]),
            conversationCount: numberValue(workspace, ["conversation_count", "conversationCount"]),
            updatedAt: nullableText(workspace, ["updated_at", "updatedAt"]),
            conversations: array(workspace.conversations).map((conversation) =>
              mapProviderConversationSummary(conversation, text(provider, ["home_directory", "homeDirectory"])),
            ),
          };
        }),
      };
    }),
  };
  // Preserve bounded-catalog metadata introduced by the shared history
  // contract. Older Daemons omit these fields, so keep them optional rather
  // than manufacturing a cursor that would change legacy behavior.
  const nextCursor = nullableNumber(source, ["next_cursor", "nextCursor"]);
  if (source.next_cursor !== undefined || source.nextCursor !== undefined) {
    mapped.nextCursor = nextCursor;
  }
  if (source.index_revision !== undefined || source.indexRevision !== undefined) {
    mapped.indexRevision = numberValue(source, ["index_revision", "indexRevision"]);
  }
  if (source.next_cursor_token !== undefined || source.nextCursorToken !== undefined) {
    mapped.nextCursorToken = nullableText(source, ["next_cursor_token", "nextCursorToken"]);
  }
  if (source.total_conversation_count !== undefined || source.totalConversationCount !== undefined) {
    mapped.totalConversationCount = numberValue(source, ["total_conversation_count", "totalConversationCount"]);
  }
  if (source.complete !== undefined) {
    mapped.complete = bool(source, ["complete"]);
  }
  return mapped;
}

export function mapManagedDecision(value: unknown): ManagedDecision {
  const source = object(value);
  return {
    id: text(source, ["decision_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    scope: mapManagedWorkScope(source.scope),
    kind: text(source, ["kind"], "general"),
    title: text(source, ["title"]),
    conclusion: text(source, ["conclusion"]),
    rationale: text(source, ["rationale"]),
    alternatives: array(source.alternatives).map((item) => {
      const alternative = object(item);
      return {
        id: text(alternative, ["alternative_id", "id"]),
        title: text(alternative, ["title"]),
        description: text(alternative, ["description"]),
      };
    }),
    consequences: stringArray(source.consequences),
    status: text(source, ["status"], "draft") as ManagedDecision["status"],
    supersededByDecisionId: nullableText(source, ["superseded_by_decision_id", "supersededByDecisionId"]),
    schemaVersion: numberValue(source, ["schema_version", "schemaVersion"], 1),
    revision: numberValue(source, ["revision"], 1),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    createdBy: mapManagedActorRef(source.created_by ?? source.createdBy),
    decidedBy: source.decided_by || source.decidedBy
      ? mapManagedActorRef(source.decided_by ?? source.decidedBy)
      : null,
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
    decidedAt: nullableText(source, ["decided_at", "decidedAt"]),
  };
}

export function mapManagedQuestion(value: unknown): ManagedQuestion {
  const source = object(value);
  return {
    id: text(source, ["question_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    scope: mapManagedWorkScope(source.scope),
    kind: text(source, ["kind"], "clarification"),
    prompt: text(source, ["prompt"]),
    reason: text(source, ["reason"]),
    choices: array(source.choices).map((item) => {
      const choice = object(item);
      return {
        id: text(choice, ["choice_id", "id"]),
        label: text(choice, ["label"]),
        description: text(choice, ["description"]),
      };
    }),
    recommendation: nullableText(source, ["recommendation"]),
    safeDefault: nullableText(source, ["safe_default", "safeDefault"]),
    status: text(source, ["status"], "open") as ManagedQuestion["status"],
    answer: nullableText(source, ["answer"]),
    answeredBy: source.answered_by || source.answeredBy
      ? mapManagedActorRef(source.answered_by ?? source.answeredBy)
      : null,
    schemaVersion: numberValue(source, ["schema_version", "schemaVersion"], 1),
    revision: numberValue(source, ["revision"], 1),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    createdBy: mapManagedActorRef(source.created_by ?? source.createdBy),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
    answeredAt: nullableText(source, ["answered_at", "answeredAt"]),
  };
}

export function mapManagedAssignment(value: unknown): ManagedAssignment {
  const source = object(value);
  const assignee = object(source.assignee);
  return {
    id: text(source, ["assignment_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    scope: mapManagedWorkScope(source.scope),
    role: text(source, ["role"], "executor"),
    assignee: {
      kind: text(assignee, ["kind"], "agent"),
      id: text(assignee, ["assignee_id", "id"]),
    },
    mandate: text(source, ["mandate"]),
    capabilityRefs: stringArray(source.capability_refs ?? source.capabilityRefs),
    contextRevision: numberValue(source, ["context_revision", "contextRevision"], 1),
    sessionId: nullableText(source, ["session_id", "sessionId"]),
    runId: nullableText(source, ["run_id", "runId"]),
    predecessorAssignmentId: nullableText(source, ["predecessor_assignment_id", "predecessorAssignmentId"]),
    leaseExpiresAt: nullableText(source, ["lease_expires_at", "leaseExpiresAt"]),
    status: text(source, ["status"], "proposed") as ManagedAssignment["status"],
    terminalReason: nullableText(source, ["terminal_reason", "terminalReason"]),
    schemaVersion: numberValue(source, ["schema_version", "schemaVersion"], 1),
    revision: numberValue(source, ["revision"], 1),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    createdBy: mapManagedActorRef(source.created_by ?? source.createdBy),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
    startedAt: nullableText(source, ["started_at", "startedAt"]),
    endedAt: nullableText(source, ["ended_at", "endedAt"]),
  };
}

function mapManagedArtifact(value: unknown): ManagedArtifact {
  const source = object(value);
  const location = object(source.location);
  return {
    id: text(source, ["artifact_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    scope: mapManagedWorkScope(source.scope),
    kind: text(source, ["kind"], "file"),
    label: text(source, ["label"]),
    location: {
      scheme: text(location, ["scheme"]),
      value: text(location, ["value"]),
    },
    digest: nullableText(source, ["digest"]),
    retention: text(source, ["retention"], "durable") as ManagedArtifact["retention"],
    status: text(source, ["status"], "candidate") as ManagedArtifact["status"],
    supersededByArtifactId: nullableText(source, ["superseded_by_artifact_id", "supersededByArtifactId"]),
    schemaVersion: numberValue(source, ["schema_version", "schemaVersion"], 1),
    revision: numberValue(source, ["revision"], 1),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    createdBy: mapManagedActorRef(source.created_by ?? source.createdBy),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
  };
}

export function mapManagedSessionLeg(value: unknown): ManagedSessionLeg {
  const source = object(value);
  return {
    id: text(source, ["session_leg_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    scope: mapManagedWorkScope(source.scope),
    sessionId: text(source, ["session_id", "sessionId"]),
    providerId: text(source, ["provider_id", "providerId"]),
    providerConversationRef: nullableText(source, ["provider_conversation_ref", "providerConversationRef"]),
    predecessorLegId: nullableText(source, ["predecessor_leg_id", "predecessorLegId"]),
    assignmentId: nullableText(source, ["assignment_id", "assignmentId"]),
    reason: text(source, ["reason"], "initial"),
    contextRevision: numberValue(source, ["context_revision", "contextRevision"], 1),
    status: text(source, ["status"], "unknown") as ManagedSessionLeg["status"],
    outcomeSummary: nullableText(source, ["outcome_summary", "outcomeSummary"]),
    schemaVersion: numberValue(source, ["schema_version", "schemaVersion"], 1),
    revision: numberValue(source, ["revision"], 1),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    createdBy: mapManagedActorRef(source.created_by ?? source.createdBy),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
    startedAt: nullableText(source, ["started_at", "startedAt"]),
    endedAt: nullableText(source, ["ended_at", "endedAt"]),
  };
}

function mapManagedRuntimeResource(value: unknown): ManagedRuntimeResource {
  const source = object(value);
  return {
    id: text(source, ["runtime_resource_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    scope: mapManagedWorkScope(source.scope),
    kind: text(source, ["kind"], "preview"),
    label: text(source, ["label"]),
    endpoint: text(source, ["endpoint"]),
    status: text(source, ["status"], "active") as ManagedRuntimeResource["status"],
    reportedState: text(source, ["reported_state", "reportedState"], "unknown") as ManagedRuntimeResource["reportedState"],
    verifiedState: text(source, ["verified_state", "verifiedState"], "not_checked") as ManagedRuntimeResource["verifiedState"],
    verifiedAt: nullableText(source, ["verified_at", "verifiedAt"]),
    schemaVersion: numberValue(source, ["schema_version", "schemaVersion"], 1),
    revision: numberValue(source, ["revision"], 1),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    createdBy: mapManagedActorRef(source.created_by ?? source.createdBy),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
  };
}

export function mapManagedChangeSet(value: unknown): ManagedChangeSet {
  const source = object(value);
  return {
    id: text(source, ["change_set_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    scope: mapManagedWorkScope(source.scope),
    title: text(source, ["title"], "未命名变更"),
    reason: text(source, ["reason"]),
    status: text(source, ["status"], "draft") as ManagedChangeSet["status"],
    baseGraphRevision: numberValue(source, ["base_graph_revision", "baseGraphRevision"], 1),
    impact: mapManagedChangeImpact(source.impact),
    appliedGraphRevision: nullableNumber(source, ["applied_graph_revision", "appliedGraphRevision"]),
    operations: array(source.operations).map(mapManagedChangeOperation),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    createdBy: mapManagedActorRef(source.created_by ?? source.createdBy),
    confirmedBy: source.confirmed_by || source.confirmedBy
      ? mapManagedActorRef(source.confirmed_by ?? source.confirmedBy)
      : null,
    confirmationNote: nullableText(source, ["confirmation_note", "confirmationNote"]),
    appliedBy: source.applied_by || source.appliedBy
      ? mapManagedActorRef(source.applied_by ?? source.appliedBy)
      : null,
    rejectedBy: source.rejected_by || source.rejectedBy
      ? mapManagedActorRef(source.rejected_by ?? source.rejectedBy)
      : null,
    rejectionReason: nullableText(source, ["rejection_reason", "rejectionReason"]),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
    confirmedAt: nullableText(source, ["confirmed_at", "confirmedAt"]),
    appliedAt: nullableText(source, ["applied_at", "appliedAt"]),
    rejectedAt: nullableText(source, ["rejected_at", "rejectedAt"]),
    revision: numberValue(source, ["revision"], 1),
  };
}

function mapManagedChangeImpact(value: unknown): ManagedChangeSet["impact"] {
  const source = object(value);
  return {
    graphRevision: numberValue(source, ["graph_revision", "graphRevision"], 0),
    operationCount: numberValue(source, ["operation_count", "operationCount"], 0),
    materialIds: stringArray(source.material_ids ?? source.materialIds),
    entityIds: stringArray(source.entity_ids ?? source.entityIds),
    roundIds: stringArray(source.round_ids ?? source.roundIds),
    futureRoundIds: stringArray(source.future_round_ids ?? source.futureRoundIds),
    completedRoundIds: stringArray(source.completed_round_ids ?? source.completedRoundIds),
    assignmentIds: stringArray(source.assignment_ids ?? source.assignmentIds),
    sessionLegIds: stringArray(source.session_leg_ids ?? source.sessionLegIds),
    computedAt: nullableText(source, ["computed_at", "computedAt"]),
  };
}

function mapManagedChangeOperation(value: unknown): ManagedChangeOperation {
  const source = object(value);
  return {
    id: text(source, ["operation_id", "id"]),
    changeSetId: text(source, ["change_set_id", "changeSetId"]),
    ordinal: numberValue(source, ["ordinal"]),
    operation: mapManagedSemanticOperation(source.operation),
    reason: text(source, ["reason"]),
    basis: mapManagedProposalBasis(source.basis),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    status: text(source, ["status"], "pending") as ManagedChangeOperation["status"],
    appliedTargetRevision: nullableNumber(source, ["applied_target_revision", "appliedTargetRevision"]),
    createdBy: mapManagedActorRef(source.created_by ?? source.createdBy),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
    revision: numberValue(source, ["revision"], 1),
  };
}

function mapManagedSemanticOperation(value: unknown): ManagedChangeOperation["operation"] {
  const source = object(value);
  const type = text(source, ["type"]);
  const expectedRevision = numberValue(source, ["expected_revision", "expectedRevision"], 1);
  if (type === "material.create") {
    return {
      type,
      operationRef: text(source, ["operation_ref", "operationRef"]),
      kind: text(source, ["kind"]),
      title: text(source, ["title"]),
      summary: text(source, ["summary"]),
      format: text(source, ["format"], "markdown"),
      body: text(source, ["body"]),
      status: text(source, ["status"], "active") === "draft" ? "draft" : "active",
      ...(typeof (source.round_id ?? source.roundId) === "string"
        ? { roundId: text(source, ["round_id", "roundId"]) }
        : {}),
      views: stringArray(source.views),
      entityRefs: array(source.entity_refs ?? source.entityRefs).map((item) => {
        const reference = object(item);
        return {
          entity: mapManagedEntityOperationRef(reference.entity),
          role: text(reference, ["role"], "content"),
          ordinal: numberValue(reference, ["ordinal"]),
        };
      }),
    };
  }
  if (type === "material.update") {
    return {
      type,
      materialId: text(source, ["material_id", "materialId"]),
      expectedRevision,
      patch: mapManagedMaterialPatch(source.patch),
    };
  }
  if (type === "material.archive") {
    return { type, materialId: text(source, ["material_id", "materialId"]), expectedRevision };
  }
  if (type === "entity.create") {
    const status = text(source, ["status"], "draft");
    return {
      type,
      operationRef: text(source, ["operation_ref", "operationRef"]),
      kind: text(source, ["kind"]),
      title: text(source, ["title"]),
      detail: text(source, ["detail"]),
      status: ["confirmed", "active", "completed"].includes(status)
        ? status as "confirmed" | "active" | "completed"
        : "draft",
      ...(typeof (source.round_id ?? source.roundId) === "string"
        ? { roundId: text(source, ["round_id", "roundId"]) }
        : {}),
      ...(source.parent ? { parent: mapManagedEntityOperationRef(source.parent) } : {}),
    };
  }
  if (type === "entity.update") {
    return {
      type,
      entityId: text(source, ["entity_id", "entityId"]),
      expectedRevision,
      patch: mapManagedEntityPatch(source.patch),
    };
  }
  if (type === "relation.create") {
    return {
      type,
      operationRef: text(source, ["operation_ref", "operationRef"]),
      source: mapManagedEntityOperationRef(source.source),
      target: mapManagedEntityOperationRef(source.target),
      relationType: text(source, ["relation_type", "relationType"]),
    };
  }
  return {
    type: "entity.archive",
    entityId: text(source, ["entity_id", "entityId"]),
    expectedRevision,
  };
}

function mapManagedEntityOperationRef(value: unknown): import("./types").ManagedEntityOperationRef {
  const source = object(value);
  if (text(source, ["kind"]) === "created") {
    return { kind: "created", operationRef: text(source, ["operation_ref", "operationRef"]) };
  }
  return {
    kind: "existing",
    entityId: text(source, ["entity_id", "entityId"]),
    expectedRevision: numberValue(source, ["expected_revision", "expectedRevision"], 1),
  };
}

function mapManagedMaterialPatch(value: unknown): ManagedMaterialPatch {
  const source = object(value);
  return {
    ...optionalStringProperty(source, "title"),
    ...optionalStringProperty(source, "summary"),
    ...optionalStringProperty(source, "format"),
    ...optionalStringProperty(source, "body"),
    ...(typeof source.status === "string" ? { status: source.status as ManagedMaterialPatch["status"] } : {}),
    ...(source.round_id || source.roundId ? { roundId: mapManagedOptionalFieldPatch(source.round_id ?? source.roundId) } : {}),
    ...(Array.isArray(source.views) ? { views: source.views.filter((item): item is string => typeof item === "string") } : {}),
    ...(Array.isArray(source.entity_refs ?? source.entityRefs) ? {
      entityRefs: array(source.entity_refs ?? source.entityRefs).map((item) => {
        const reference = object(item);
        return {
          entityId: text(reference, ["entity_id", "entityId"]),
          expectedRevision: numberValue(reference, ["expected_revision", "expectedRevision"], 1),
          role: text(reference, ["role"], "content"),
          ordinal: numberValue(reference, ["ordinal"]),
        };
      }),
    } : {}),
  };
}

function mapManagedEntityPatch(value: unknown): ManagedEntityPatch {
  const source = object(value);
  return {
    ...optionalStringProperty(source, "title"),
    ...optionalStringProperty(source, "detail"),
    ...(typeof source.status === "string" ? { status: source.status as ManagedEntityPatch["status"] } : {}),
    ...(source.round_id || source.roundId ? { roundId: mapManagedOptionalFieldPatch(source.round_id ?? source.roundId) } : {}),
    ...(source.parent_entity_id || source.parentEntityId
      ? { parentEntityId: mapManagedOptionalFieldPatch(source.parent_entity_id ?? source.parentEntityId) }
      : {}),
  };
}

function mapManagedOptionalFieldPatch(value: unknown): ManagedOptionalFieldPatch {
  const source = object(value);
  return text(source, ["op"], "clear") === "set"
    ? { op: "set", value: text(source, ["value"]) }
    : { op: "clear" };
}

function mapManagedProposalBasis(value: unknown): ManagedProposalBasis {
  const source = object(value);
  return text(source, ["type"], "explicit") === "inferred"
    ? { type: "inferred", confidenceBps: numberValue(source, ["confidence_bps", "confidenceBps"]) }
    : { type: "explicit" };
}

function mapManagedWorkScope(value: unknown): ManagedWorkScopeRef {
  const source = object(value);
  const type = text(source, ["type"], "work");
  if (type === "round") return { type, roundId: text(source, ["round_id", "roundId"]) };
  if (type === "entity") return { type, entityId: text(source, ["entity_id", "entityId"]) };
  return { type: "work", workId: text(source, ["work_id", "workId"]) };
}

function optionalStringProperty(source: JsonObject, key: string): Record<string, string> {
  return typeof source[key] === "string" ? { [key]: source[key] as string } : {};
}

export function mapWorkGraphPage(value: unknown): WorkGraphPage {
  const source = object(value);
  return {
    workId: text(source, ["work_id", "workId"]),
    graphRevision: numberValue(source, ["graph_revision", "graphRevision"]),
    resource: text(source, ["resource"], "entities") as WorkGraphPage["resource"],
    materials: array(source.materials).map(mapManagedMaterial),
    entities: array(source.entities).map(mapManagedGraphEntity),
    relations: array(source.relations).map(mapManagedRelation),
    materialEntityRefs: array(source.material_entity_refs ?? source.materialEntityRefs)
      .map(mapManagedMaterialEntityRef),
    nextCursor: nullableText(source, ["next_cursor", "nextCursor"]),
  };
}

export function mapWorkMutationPage(value: unknown): WorkMutationPage {
  const source = object(value);
  return {
    workId: text(source, ["work_id", "workId"]),
    view: text(source, ["view"], "batches") as WorkMutationPage["view"],
    batches: array(source.batches).map(mapWorkMutationBatch),
    operations: array(source.operations).map(mapWorkMutationOperation),
    nextCursor: nullableText(source, ["next_cursor", "nextCursor"]),
  };
}

function mapWorkMutationBatch(value: unknown): WorkMutationBatch {
  const source = object(value);
  return {
    id: text(source, ["batch_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    title: text(source, ["title"]),
    reason: text(source, ["reason"]),
    actor: mapManagedActorRef(source.actor),
    graphRevisionBefore: nullableNumber(source, ["graph_revision_before", "graphRevisionBefore"]),
    graphRevisionAfter: nullableNumber(source, ["graph_revision_after", "graphRevisionAfter"]),
    triggerRefs: array(source.trigger_refs ?? source.triggerRefs).map(mapManagedSourceRef),
    confirmationRefs: array(source.confirmation_refs ?? source.confirmationRefs).map(mapManagedSourceRef),
    changeSetId: nullableText(source, ["change_set_id", "changeSetId"]),
    roundId: nullableText(source, ["round_id", "roundId"]),
    correlationId: nullableText(source, ["correlation_id", "correlationId"]),
    causationId: nullableText(source, ["causation_id", "causationId"]),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    appliedAt: timestamp(source, ["applied_at", "appliedAt"]),
    operations: array(source.operations).map(mapWorkMutationOperation),
  };
}

function mapWorkMutationOperation(value: unknown): WorkMutationOperation {
  const source = object(value);
  return {
    id: text(source, ["operation_id", "id"]),
    batchId: text(source, ["batch_id", "batchId"]),
    ordinal: numberValue(source, ["ordinal"]),
    targetType: text(source, ["target_type", "targetType"]),
    targetId: text(source, ["target_id", "targetId"]),
    targetTitle: text(source, ["target_title", "targetTitle"]),
    materialId: nullableText(source, ["material_id", "materialId"]),
    roundId: nullableText(source, ["round_id", "roundId"]),
    operationType: text(source, ["operation_type", "operationType"]),
    changedPaths: stringArray(source.changed_paths ?? source.changedPaths),
    targetRevisionBefore: nullableNumber(source, ["target_revision_before", "targetRevisionBefore"]),
    targetRevisionAfter: nullableNumber(source, ["target_revision_after", "targetRevisionAfter"]),
    beforeSnapshot: source.before_snapshot ?? source.beforeSnapshot ?? null,
    afterSnapshot: source.after_snapshot ?? source.afterSnapshot ?? null,
    reason: text(source, ["reason"]),
    actor: mapManagedActorRef(source.actor),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    occurredAt: timestamp(source, ["occurred_at", "occurredAt"]),
  };
}

function mapManagedMaterial(value: unknown): ManagedMaterial {
  const source = object(value);
  return {
    id: text(source, ["material_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    roundId: nullableText(source, ["round_id", "roundId"]),
    kind: text(source, ["kind"], "general"),
    title: text(source, ["title"], "未命名资料"),
    summary: text(source, ["summary"]),
    format: text(source, ["format"], "markdown"),
    body: text(source, ["body"]),
    status: text(source, ["status"], "active") as ManagedMaterial["status"],
    views: array(source.views).filter((item): item is string => typeof item === "string"),
    schemaVersion: numberValue(source, ["schema_version", "schemaVersion"], 1),
    revision: numberValue(source, ["revision"], 1),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    createdBy: mapManagedActorRef(source.created_by ?? source.createdBy),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
  };
}

function mapManagedGraphEntity(value: unknown): ManagedGraphEntity {
  const source = object(value);
  return {
    id: text(source, ["entity_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    roundId: nullableText(source, ["round_id", "roundId"]),
    parentEntityId: nullableText(source, ["parent_entity_id", "parentEntityId"]),
    kind: text(source, ["kind"], "unknown"),
    title: text(source, ["title"], "未命名条目"),
    detail: text(source, ["detail"]),
    status: text(source, ["status"], "draft") as ManagedGraphEntity["status"],
    schemaVersion: numberValue(source, ["schema_version", "schemaVersion"], 1),
    revision: numberValue(source, ["revision"], 1),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    createdBy: mapManagedActorRef(source.created_by ?? source.createdBy),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
  };
}

function mapManagedRelation(value: unknown): ManagedRelation {
  const source = object(value);
  return {
    id: text(source, ["relation_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    sourceEntityId: text(source, ["source_entity_id", "sourceEntityId"]),
    targetEntityId: text(source, ["target_entity_id", "targetEntityId"]),
    relationType: text(source, ["relation_type", "relationType"]),
    status: text(source, ["status"], "active") as ManagedRelation["status"],
    schemaVersion: numberValue(source, ["schema_version", "schemaVersion"], 1),
    revision: numberValue(source, ["revision"], 1),
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    createdBy: mapManagedActorRef(source.created_by ?? source.createdBy),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
  };
}

function mapManagedMaterialEntityRef(value: unknown): ManagedMaterialEntityRef {
  const source = object(value);
  return {
    materialId: text(source, ["material_id", "materialId"]),
    materialRevision: numberValue(source, ["material_revision", "materialRevision"], 1),
    entityId: text(source, ["entity_id", "entityId"]),
    entityRevision: numberValue(source, ["entity_revision", "entityRevision"], 1),
    role: text(source, ["role"], "content"),
    ordinal: numberValue(source, ["ordinal"]),
  };
}

function mapManagedSourceRef(value: unknown): ManagedMaterial["sourceRefs"][number] {
  const source = object(value);
  return {
    kind: text(source, ["kind"], "manual_edit") as ManagedMaterial["sourceRefs"][number]["kind"],
    sourceId: text(source, ["source_id", "sourceId"]),
    label: nullableText(source, ["label"]),
  };
}

function mapManagedActorRef(value: unknown): ManagedMaterial["createdBy"] {
  const source = object(value);
  return {
    kind: text(source, ["kind"], "system") as ManagedMaterial["createdBy"]["kind"],
    actorId: text(source, ["actor_id", "actorId"], "unknown"),
  };
}

function mapManagedWorkIdentity(value: unknown): ManagedWorkIdentity {
  const source = object(value);
  return {
    id: text(source, ["work_id", "id"]),
    title: text(source, ["title"], "Orni Work"),
    objective: text(source, ["objective"]),
    status: text(source, ["status"], "active") as ManagedWorkIdentity["status"],
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
    revision: numberValue(source, ["revision"], 1),
  };
}

function mapManagedRound(value: unknown): ManagedRound {
  const source = object(value);
  const outcome = object(source.outcome);
  return {
    id: text(source, ["round_id", "id"]),
    workId: text(source, ["work_id", "workId"]),
    ordinal: numberValue(source, ["ordinal"], 1),
    title: text(source, ["title"], "未命名 Round"),
    objective: text(source, ["objective"]),
    successCriteria: array(source.success_criteria ?? source.successCriteria)
      .filter((item): item is string => typeof item === "string"),
    status: text(source, ["status"], "planned") as ManagedRound["status"],
    progress: numberValue(source, ["progress"]),
    baselineRevision: numberValue(source, ["baseline_revision", "baselineRevision"], 1),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
    startedAt: nullableText(source, ["started_at", "startedAt"]),
    endedAt: nullableText(source, ["ended_at", "endedAt"]),
    outcome: source.outcome ? {
      summary: text(outcome, ["summary"]),
      baselineUpdates: array(outcome.baseline_updates ?? outcome.baselineUpdates)
        .filter((item): item is string => typeof item === "string"),
      recordedAt: timestamp(outcome, ["recorded_at", "recordedAt"]),
    } : null,
    sourceRefs: array(source.source_refs ?? source.sourceRefs).map(mapManagedSourceRef),
    revision: numberValue(source, ["revision"], 1),
  };
}

function mapManagedTask(value: unknown): ManagedTask {
  const source = object(value);
  return {
    id: text(source, ["task_id", "id"]),
    sessionId: text(source, ["session_id", "sessionId"]),
    title: text(source, ["title"]),
    objective: text(source, ["objective"]),
    instructions: text(source, ["instructions"]),
    constraints: array(source.constraints).filter((item): item is string => typeof item === "string"),
    deliverables: array(source.deliverables).filter((item): item is string => typeof item === "string"),
    state: text(source, ["state"], "draft") as ManagedTask["state"],
    activeRunId: nullableText(source, ["active_run_id", "activeRunId"]),
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
    revision: numberValue(source, ["revision"], 1),
  };
}

function mapManagedRun(value: unknown): ManagedRun {
  const source = object(value);
  return {
    id: text(source, ["run_id", "id"]),
    taskId: text(source, ["task_id", "taskId"]),
    sessionId: text(source, ["session_id", "sessionId"]),
    desiredState: text(source, ["desired_state", "desiredState"], "active") as ManagedRun["desiredState"],
    managementState: text(source, ["management_state", "managementState"], "unknown") as ManagedRun["managementState"],
    health: text(source, ["health"], "unknown") as ManagedRun["health"],
    completionBasis: nullableText(source, ["completion_basis", "completionBasis"]) as ManagedRun["completionBasis"],
    lastDispatchId: nullableText(source, ["last_dispatch_id", "lastDispatchId"]),
    lastReportId: nullableText(source, ["last_report_id", "lastReportId"]),
    startedAt: nullableText(source, ["started_at", "startedAt"]),
    endedAt: nullableText(source, ["ended_at", "endedAt"]),
    updatedAt: timestamp(source, ["updated_at", "updatedAt"]),
    revision: numberValue(source, ["revision"], 1),
  };
}

function mapManagedDispatch(value: unknown): ManagedDispatch {
  const source = object(value);
  return {
    id: text(source, ["dispatch_id", "id"]),
    runId: text(source, ["run_id", "runId"]),
    sequence: numberValue(source, ["sequence"]),
    kind: text(source, ["kind"], "follow_up") as ManagedDispatch["kind"],
    content: text(source, ["content"]),
    deliveryState: text(source, ["delivery_state", "deliveryState"], "queued") as ManagedDispatch["deliveryState"],
    createdAt: timestamp(source, ["created_at", "createdAt"]),
  };
}

function mapManagedReport(value: unknown): ManagedReport {
  const source = object(value);
  const question = object(source.question);
  const stage = object(source.stage);
  return {
    id: text(source, ["report_id", "id"]),
    runId: text(source, ["run_id", "runId"]),
    dispatchId: text(source, ["dispatch_id", "dispatchId"]),
    sequence: numberValue(source, ["sequence"]),
    type: text(source, ["report_type", "type"], "progress"),
    summary: nullableText(source, ["summary"]),
    completedItems: array(source.completed_items ?? source.completedItems)
      .filter((item): item is string => typeof item === "string"),
    nextAction: nullableText(source, ["next_action", "nextAction"]),
    question: source.question ? {
      id: text(question, ["question_id", "id"]),
      prompt: text(question, ["prompt"]),
      choices: array(question.choices).filter((item): item is string => typeof item === "string"),
    } : null,
    source: text(source, ["source"], "assistant_frame"),
    confidence: text(source, ["confidence"], "reported"),
    receivedAt: timestamp(source, ["received_at", "receivedAt"]),
    stage: Object.keys(stage).length ? {
      id: nullableText(stage, ["stage_id", "id"]),
      label: nullableText(stage, ["label"]),
      current: nullableNumber(stage, ["current"]),
      total: nullableNumber(stage, ["total"]),
    } : null,
  };
}

function mapManagedAttention(value: unknown): ManagedAttention {
  const source = object(value);
  return {
    id: text(source, ["attention_id", "id"]),
    runId: text(source, ["run_id", "runId"]),
    reportId: nullableText(source, ["report_id", "reportId"]),
    type: text(source, ["attention_type", "type"]),
    prompt: text(source, ["prompt"]),
    status: text(source, ["status"], "open") as ManagedAttention["status"],
    createdAt: timestamp(source, ["created_at", "createdAt"]),
    resolvedAt: nullableText(source, ["resolved_at", "resolvedAt"]),
    revision: numberValue(source, ["revision"], 1),
  };
}

export function mapEvent(value: unknown): OrniEvent | null {
  const outer = object(value);
  if (text(outer, ["type"]) === "event" && outer.event) return mapEvent(outer.event);
  const envelope = outer;
  const payload = object(envelope.payload);
  const type = text(envelope, ["type", "event_type", "kind"]);
  const revision = numberValue(envelope, ["revision", "global_sequence"], Date.now());
  if (type === "provider_fact_recorded" || type === "provider.fact.recorded") {
    return {
      type: "session.detail.invalidated",
      revision,
      sessionId: text(payload, ["session_id", "sessionId"]),
      factKind: text(payload, ["kind"], "unknown"),
    };
  }
  if (type === "session_created" || type === "session.created") {
    return { type: "session.created", revision, session: mapSessionSummary(payload.session ?? payload) };
  }
  if (type === "session_updated" || type === "session.updated") {
    return { type: "session.updated", revision, session: mapSessionSummary(payload.session ?? payload) };
  }
  if (type === "session_deleted" || type === "session.deleted") {
    return { type: "session.deleted", revision, sessionId: text(payload, ["session_id", "sessionId", "id"]) };
  }
  if (type === "terminal_appended" || type === "terminal.appended") {
    return {
      type: "terminal.appended",
      revision,
      sessionId: text(payload, ["session_id", "sessionId"]),
      lines: array(payload.lines).map(mapTerminalLine),
    };
  }
  if (type === "notification_created" || type === "notification.created") {
    return { type: "notification.created", revision, notification: mapNotification(payload.notification ?? payload) };
  }
  if (type === "notification_updated" || type === "notification.updated") {
    return { type: "notification.updated", revision, notification: mapNotification(payload.notification ?? payload) };
  }
  return type ? { type: "snapshot.invalidated", revision } : null;
}
